/**
 * WP-16: the replay timeline, a match-replay bar along the bottom of the page. The track carries a mark for
 * every overlap, negotiation move, failed verdict and land; the slider scrubs; the buttons play, pause, step and
 * set the speed; the jump row moves to the previous or next mark of each kind.
 *
 * Markup only, escaped like every other view. The slider position, elapsed time and caption change every frame,
 * so app.ts updates those in place instead of re-rendering this.
 */
import { pendingPlans, pendingPromotions } from "./approvals.ts";
import { html, type Html } from "../html.ts";
import { MARKER_KINDS, MARKER_LABEL, type MarkerKind } from "../recording.ts";
import type { DashboardState } from "../state.ts";
import { shortSha } from "./common.ts";

export const REPLAY_SPEEDS = [1, 2, 5, 10, 20] as const;

export interface TimelineMark {
  kind: MarkerKind;
  /** Recording ms from the first event. */
  offset: number;
  text: string;
}

export interface TimelineControls {
  playing: boolean;
  finished: boolean;
  speed: number;
  capGaps: boolean;
  /** Read-only share page: no loading other logs. */
  shared: boolean;
  atStart: boolean;
}

/** `m:ss`, or `h:mm:ss` past an hour. With `tenths`, `m:ss.s`, for runs short enough that seconds are coarse. */
export function duration(ms: number, tenths = false): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, "0") + (tenths ? `.${Math.floor((Math.max(0, ms) % 1000) / 100)}` : "");
  return h ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
}

/** The marks on the track, one lane per kind, as SVG (attributes only, so the CSP needs no inline styles). */
export function marksView(marks: readonly TimelineMark[], total: number): Html {
  const lane = (k: MarkerKind) => MARKER_KINDS.indexOf(k);
  const x = (offset: number) => (total > 0 ? Math.min(998, Math.max(0, (offset / total) * 1000 - 1)) : 0);
  return html`<svg class="tl-marks" viewBox="0 0 1000 24" preserveAspectRatio="none" aria-hidden="true">${marks.map(
    (m) => html`<rect class="mk ${m.kind}" x="${x(m.offset).toFixed(1)}" y="${lane(m.kind) * 6}" width="2" height="6"><title>${m.text}</title></rect>`,
  )}</svg>`;
}

export function controlsView(c: TimelineControls): Html {
  return html`<div class="tl-buttons">
      <button type="button" data-action="replay-restart" title="Back to the start" aria-label="Back to the start">&#9198;</button>
      <button type="button" data-action="replay-back" title="Previous moment (left arrow)" aria-label="Previous moment"${c.atStart ? " disabled" : ""}>&#9664;&#9664;</button>
      <button type="button" class="play" data-action="replay-toggle" aria-label="${c.playing ? "Pause" : "Play"}">${c.playing ? html`&#10074;&#10074; Pause` : html`&#9654; Play`}</button>
      <button type="button" data-action="replay-step" title="Next moment (right arrow)" aria-label="Next moment"${c.finished ? " disabled" : ""}>&#9654;&#9654;</button>
    </div>
    <div class="tl-speeds" role="group" aria-label="Speed">${REPLAY_SPEEDS.map(
      (x) => html`<button type="button" data-action="replay-speed" data-speed="${x}" class="${x === c.speed ? "on" : ""}" aria-pressed="${x === c.speed ? "true" : "false"}">${x}x</button>`,
    )}</div>
    <select class="tl-speed-select" data-action="replay-speed-select" aria-label="Speed">${REPLAY_SPEEDS.map(
      (x) => html`<option value="${x}"${x === c.speed ? " selected" : ""}>${x}x</option>`,
    )}</select>
    <button type="button" class="tl-more-toggle" data-action="tl-more" aria-label="More replay options">&#8943;</button>
    <div class="tl-extra">
      <label class="cap" title="Shorten quiet stretches longer than 2 seconds (off: true timing)"><input type="checkbox" data-action="replay-cap"${c.capGaps ? " checked" : ""} /> skip quiet gaps</label>
      <button type="button" data-action="download" title="Download this run's events">Save log</button>
      ${c.shared ? html`<button type="button" data-action="copy-moment" title="Copy a link that opens this replay at this moment">Link to this moment</button>` : html`<label class="file">Load log<input type="file" id="recording" accept="application/json,.json" /></label>`}
    </div>`;
}

/** Previous and next mark of each kind from the current position. Kinds with no marks are left out. */
export function jumpsView(marks: readonly TimelineMark[], position: number): Html {
  const kinds = MARKER_KINDS.filter((k) => marks.some((m) => m.kind === k));
  if (!kinds.length) return html``;
  return html`<span class="tl-jump-label">Jump to</span>${kinds.map((k) => {
    const all = marks.filter((m) => m.kind === k);
    const label = MARKER_LABEL[k];
    const hasPrev = all.some((m) => m.offset < position);
    const hasNext = all.some((m) => m.offset > position);
    return html`<span class="jump ${k}">
      <button type="button" data-action="jump" data-kind="${k}" data-dir="-1" aria-label="Previous ${label.one.toLowerCase()}"${hasPrev ? "" : " disabled"}>&#8249;</button>
      <span class="what"><i class="dot" aria-hidden="true"></i>${all.length === 1 ? label.one : label.many} <b>${all.length}</b></span>
      <button type="button" data-action="jump" data-kind="${k}" data-dir="1" aria-label="Next ${label.one.toLowerCase()}"${hasNext ? "" : " disabled"}>&#8250;</button>
    </span>`;
  })}`;
}

/** The mark to jump to: the nearest one of `kind` before or after `position`, or null. */
export function jumpTarget(marks: readonly TimelineMark[], kind: MarkerKind, dir: 1 | -1, position: number): TimelineMark | null {
  const all = marks.filter((m) => m.kind === kind);
  if (dir > 0) return all.find((m) => m.offset > position) ?? null;
  return all.filter((m) => m.offset < position).at(-1) ?? null;
}

/**
 * What waited on a human at this point of the recording, without a single button or answer field. A replay
 * shows the approvals; it can never make one.
 */
export function waitingView(state: DashboardState): Html {
  const plans = pendingPlans(state);
  const promotions = pendingPromotions(state);
  if (!plans.length && !promotions.length) return html``;
  return html`<div class="approvals read-only">
    ${plans.map((p) => html`<div class="approval"><div class="what"><strong>At this point the work plan was waiting for approval:</strong> ${p.title} <span class="muted">(${p.packages.length} packages)</span></div></div>`)}
    ${promotions.map((d) => html`<div class="approval"><div class="what"><strong>At this point a preview was waiting for approval to promote:</strong> ${d.stack} <code>${d.branch}</code> at <code>${shortSha(d.commit)}</code></div></div>`)}
  </div>`;
}
